/**
 * Animation System for Flowi
 * Main entry point for all animation features
 */

// Core exports
export { AnimationEngine } from './AnimationEngine';
export * from './animationTypes';
export { easings, bezierPresets, cubicBezier } from './easings';
export * from './utils';

// Animation implementations
export { DataFlowAnimation, createDataFlowAnimation } from './dataFlowAnimation';
export {
  PulseAnimation,
  createPulseAnimation,
  createHeartbeatAnimation,
  createBreathingAnimation
} from './pulseAnimation';
export {
  PathAnimation,
  createBezierPath,
  createArcPath
} from './pathAnimation';

// Singleton instance for easy access
import { AnimationEngine } from './AnimationEngine';
import { DataFlowAnimation } from './dataFlowAnimation';
import { PulseAnimation } from './pulseAnimation';
import { PathAnimation, PathAnimationConfig, PathPoint } from './pathAnimation';
import {
  AnimationTarget,
  AnimationConfig,
  DataFlowConfig,
  PulseConfig
} from './animationTypes';
import { ViewItem, Connector, Coords } from 'src/types';

// Global animation engine instance
let globalEngine: AnimationEngine | null = null;

/**
 * Get or create the global animation engine
 */
export function getAnimationEngine(): AnimationEngine {
  if (!globalEngine) {
    globalEngine = new AnimationEngine();
  }
  return globalEngine;
}

/**
 * High-level animation API
 */
export const animations = {
  /**
   * Animate any property
   */
  animate(
    target: AnimationTarget,
    properties: Record<string, number>,
    config?: AnimationConfig
  ): string {
    return getAnimationEngine().animate(target, properties, config);
  },

  /**
   * Stop an animation
   */
  stop(id: string, jumpToEnd?: boolean): void {
    getAnimationEngine().stop(id, jumpToEnd);
  },

  /**
   * Stop all animations
   */
  stopAll(jumpToEnd?: boolean): void {
    getAnimationEngine().stopAll(jumpToEnd);
  },

  /**
   * Pause an animation
   */
  pause(id: string): void {
    getAnimationEngine().pause(id);
  },

  /**
   * Resume an animation
   */
  resume(id: string): void {
    getAnimationEngine().resume(id);
  },

  /**
   * Data flow animations
   */
  dataFlow(connector: Connector, path: Coords[], config?: DataFlowConfig): string {
    const engine = getAnimationEngine();
    const dataFlow = new DataFlowAnimation(engine);
    return dataFlow.startDataFlow(connector, path, config);
  },

  /**
   * Pulse animations
   */
  pulse(target: AnimationTarget, config?: PulseConfig): string {
    const engine = getAnimationEngine();
    const pulse = new PulseAnimation(engine);
    return pulse.startPulse(target, config);
  },

  heartbeat(target: AnimationTarget, config?: PulseConfig): string {
    const engine = getAnimationEngine();
    const pulse = new PulseAnimation(engine);
    return pulse.startHeartbeat(target, config);
  },

  breathing(target: AnimationTarget, config?: PulseConfig): string {
    const engine = getAnimationEngine();
    const pulse = new PulseAnimation(engine);
    return pulse.startBreathing(target, config);
  },

  /**
   * Path animations
   */
  followPath(target: ViewItem, path: PathPoint[], config?: PathAnimationConfig): string {
    const engine = getAnimationEngine();
    const pathAnim = new PathAnimation(engine);
    return pathAnim.animateAlongPath(target, path, config);
  },

  circular(target: ViewItem, center: Coords, radius: number, config?: PathAnimationConfig): string {
    const engine = getAnimationEngine();
    const pathAnim = new PathAnimation(engine);
    return pathAnim.animateCircular(target, center, radius, config);
  },

  figure8(target: ViewItem, center: Coords, width: number, height: number, config?: PathAnimationConfig): string {
    const engine = getAnimationEngine();
    const pathAnim = new PathAnimation(engine);
    return pathAnim.animateFigure8(target, center, width, height, config);
  },

  spiral(
    target: ViewItem,
    center: Coords,
    startRadius: number,
    endRadius: number,
    revolutions?: number,
    config?: PathAnimationConfig
  ): string {
    const engine = getAnimationEngine();
    const pathAnim = new PathAnimation(engine);
    return pathAnim.animateSpiral(target, center, startRadius, endRadius, revolutions, config);
  },

  /**
   * Common animations
   */
  fadeIn(target: AnimationTarget, duration: number = 500): string {
    return getAnimationEngine().animate(
      target,
      { opacity: 1 },
      { duration, easing: 'easeOut' }
    );
  },

  fadeOut(target: AnimationTarget, duration: number = 500): string {
    return getAnimationEngine().animate(
      target,
      { opacity: 0 },
      { duration, easing: 'easeIn' }
    );
  },

  slideIn(target: ViewItem, from: 'left' | 'right' | 'top' | 'bottom', distance: number = 100, duration: number = 600): string {
    const startOffset = { x: 0, y: 0 };

    switch (from) {
      case 'left': startOffset.x = -distance; break;
      case 'right': startOffset.x = distance; break;
      case 'top': startOffset.y = -distance; break;
      case 'bottom': startOffset.y = distance; break;
    }

    const originalPosition = { ...target.tile };
    target.tile.x += startOffset.x;
    target.tile.y += startOffset.y;

    return getAnimationEngine().animate(
      target.tile,
      { x: originalPosition.x, y: originalPosition.y },
      { duration, easing: 'easeOutCubic' }
    );
  },

  shake(target: ViewItem, intensity: number = 10, duration: number = 500): string {
    const originalPosition = { ...target.tile };
    let shakeCount = 0;
    const shakes = 10;

    return getAnimationEngine().animate(
      target,
      { _shakeProgress: 1 },
      {
        duration,
        easing: 'linear',
        onUpdate: (progress) => {
          const currentShake = Math.floor(progress * shakes);
          if (currentShake !== shakeCount) {
            shakeCount = currentShake;
            const angle = Math.random() * Math.PI * 2;
            const distance = intensity * (1 - progress); // Decay

            target.tile.x = originalPosition.x + Math.cos(angle) * distance;
            target.tile.y = originalPosition.y + Math.sin(angle) * distance;
          }
        },
        onComplete: () => {
          target.tile = originalPosition;
        }
      }
    );
  },

  bounce(target: ViewItem, height: number = 30, duration: number = 600): string {
    const originalY = target.tile.y;

    return getAnimationEngine().animate(
      target.tile,
      { y: originalY - height },
      {
        duration: duration / 2,
        easing: 'easeOutQuad',
        onComplete: () => {
          getAnimationEngine().animate(
            target.tile,
            { y: originalY },
            {
              duration: duration / 2,
              easing: 'easeOutBounce'
            }
          );
        }
      }
    );
  },

  /**
   * Group animations
   */
  parallel(animations: Array<() => string>): string[] {
    return animations.map(anim => anim());
  },

  sequence(animations: Array<() => string>, delay: number = 0): void {
    animations.forEach((anim, index) => {
      setTimeout(() => anim(), index * delay);
    });
  },

  stagger(
    targets: AnimationTarget[],
    properties: Record<string, number>,
    staggerDelay: number = 50,
    config?: AnimationConfig
  ): string[] {
    return targets.map((target, index) =>
      getAnimationEngine().animate(target, properties, {
        ...config,
        delay: (config?.delay || 0) + index * staggerDelay
      })
    );
  }
};

// Export type for the animations object
export type AnimationsAPI = typeof animations;

// Export React hook
export { useAnimation } from './useAnimation';